/**
 * 법령 요율 localStorage 캐시
 *
 * - Firestore 도달 전 즉시 가용한 값을 보관 (오프라인·새로고침 직후 안정성).
 * - 손상된 JSON 발견 시 자동 삭제 (다음 syncOnce 가 default → Firestore 로 재생성).
 * - quota exceeded / private mode 등 실패는 모두 graceful — 절대 throw 하지 않는다.
 */

import type { RegulatoryRates } from '../../types/regulatoryRates';

const KEY_PREFIX = 'regulatory_rates_';
const KEY = (year: number) => `${KEY_PREFIX}${year}`;

export function loadFromCache(year: number): RegulatoryRates | null {
  try {
    const raw = localStorage.getItem(KEY(year));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as RegulatoryRates;
    // meta 가 없거나 year 가 어긋나면 손상된 것으로 간주
    if (!parsed?.meta || parsed.meta.year !== year) {
      localStorage.removeItem(KEY(year));
      return null;
    }
    return parsed;
  } catch (e) {
    console.warn('[rateCache] load failed, removing corrupted entry:', e);
    try {
      localStorage.removeItem(KEY(year));
    } catch {
      /* ignore */
    }
    return null;
  }
}

export function saveToCache(year: number, rates: RegulatoryRates): void {
  try {
    localStorage.setItem(KEY(year), JSON.stringify(rates));
  } catch (e) {
    console.warn('[rateCache] save failed:', e);
  }
}

export function clearCache(year?: number): void {
  try {
    if (year !== undefined) {
      localStorage.removeItem(KEY(year));
      return;
    }
    Object.keys(localStorage)
      .filter((k) => k.startsWith(KEY_PREFIX))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) {
    console.warn('[rateCache] clear failed:', e);
  }
}
